import React, { useEffect, useState } from 'react';

const SLIDES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_monline-revival/artifacts/cv61btjt_WhatsApp%20Image%202026-05-23%20at%2007.40.31.jpeg',
    alt: 'New Realm Online — Are you ready for adventure?',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_monline-revival/artifacts/3ik2umg4_WhatsApp%20Image%202026-05-23%20at%2007.39.54.jpeg',
    alt: 'New Realm Online — Caught in a sunny meadow with a sassy NPC',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_monline-revival/artifacts/e7vm6og0_WhatsApp%20Image%202026-05-23%20at%2007.39.41.jpeg',
    alt: 'New Realm Online — Angry Slime waiting to be farmed',
  },
  {
    src: '/images/hero_backdrop.jpg',
    alt: 'New Realm Online — Founder village scene',
  },
];

const INTERVAL_MS = 5000;

/**
 * Auto-rotating gameplay teaser slideshow.
 * - Each image is shown at its FULL original aspect ratio (no cropping)
 * - Smooth crossfade between slides
 * - Pauses while the user is hovering or has tabbed away
 * - Dot indicators are clickable to jump
 */
const GameplaySlideshow = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // Map of slide index → natural aspect ratio (width / height). Used to drive
  // the wrapper height so we never crop the active image.
  const [ratios, setRatios] = useState({});

  // Preload images and capture each natural aspect ratio
  useEffect(() => {
    SLIDES.forEach((s, i) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setRatios((prev) =>
            prev[i] ? prev : { ...prev, [i]: img.naturalWidth / img.naturalHeight }
          );
        }
      };
      img.src = s.src;
    });
  }, []);

  // Visibility-aware auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setActive((i) => (i + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  // Fallback to 1:1 until the active image's natural size is known.
  const activeRatio = ratios[active] || 1;

  return (
    <section
      className="relative w-full bg-[#0a0608] py-16 md:py-24 overflow-hidden"
      data-testid="gameplay-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-8 fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">A GLIMPSE OF</p>
          <h2 className="font-heading section-heading-white mt-2">THE</h2>
          <h2 className="font-heading section-heading-gold">GAMEPLAY</h2>
        </div>

        <div
          className="relative w-full max-w-[720px] mx-auto rounded-xl overflow-hidden border border-white/10 bg-black shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] transition-[aspect-ratio] duration-500"
          style={{ aspectRatio: String(activeRatio) }}
        >
          {SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.alt}
              draggable="false"
              loading={i === 0 ? 'eager' : 'lazy'}
              data-testid={`gameplay-slide-${i}`}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ease-in-out ${
                active === i ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}

          {/* Subtle bottom-edge vignette for readability of dot row */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"
          />

          {/* Dot navigation */}
          <div
            className="absolute bottom-4 left-0 right-0 flex justify-center gap-2.5"
            role="tablist"
            aria-label="Gameplay slide"
          >
            {SLIDES.map((_, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`Show slide ${i + 1}`}
                  data-testid={`gameplay-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    isActive ? 'bg-[#f5b400] w-7' : 'bg-white/40 hover:bg-white/70 w-2'
                  }`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameplaySlideshow;
